let cachedAudioBuffer: AudioBuffer | null = null;
let cachedObjectUrl: string | null = null;
let isLoading = false;
let loadPromise: Promise<AudioBuffer> | null = null;

/**
 * Preloads and decodes audio file for immediate playback
 * Caches the decoded AudioBuffer for reuse
 */
export async function preloadAudio(
  audioPath: string,
  audioContext: AudioContext
): Promise<AudioBuffer> {
  if (cachedAudioBuffer) {
    return cachedAudioBuffer;
  }

  if (isLoading && loadPromise) {
    return loadPromise;
  }

  isLoading = true;
  loadPromise = (async () => {
    try {
      const response = await fetch(audioPath);
      const arrayBuffer = await response.arrayBuffer();
      const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);

      cachedAudioBuffer = audioBuffer;
      isLoading = false;

      return audioBuffer;
    } catch (error) {
      isLoading = false;
      loadPromise = null;
      throw error;
    }
  })();

  return loadPromise;
}

/**
 * Creates an AudioBufferSourceNode from preloaded audio for immediate playback
 */
export function createAudioBufferSource(
  audioContext: AudioContext,
  audioBuffer: AudioBuffer,
  gainValue = 2.0
): {
  source: AudioBufferSourceNode;
  gainNode: GainNode;
} {
  const source = audioContext.createBufferSource();
  const gainNode = audioContext.createGain();

  source.buffer = audioBuffer;
  gainNode.gain.value = gainValue;
  source.connect(gainNode);
  gainNode.connect(audioContext.destination);

  return { gainNode, source };
}

/**
 * Plays preloaded audio buffer immediately
 */
export function playPreloadedAudio(
  audioContext: AudioContext,
  audioBuffer: AudioBuffer,
  gainValue = 2.0
): Promise<void> {
  return new Promise((resolve, reject) => {
    try {
      const { source } = createAudioBufferSource(
        audioContext,
        audioBuffer,
        gainValue
      );

      source.onended = () => {
        resolve();
      };

      try {
        source.start(0);
      } catch (startError) {
        reject(
          startError instanceof Error
            ? startError
            : new Error('Audio playback failed to start')
        );
      }
    } catch (error) {
      reject(error instanceof Error ? error : new Error(String(error)));
    }
  });
}

/**
 * Clears cached audio buffer
 */
export function clearPreloadedAudio(): void {
  cachedAudioBuffer = null;

  if (cachedObjectUrl) {
    URL.revokeObjectURL(cachedObjectUrl);
    cachedObjectUrl = null;
  }
}
